import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useDebouncedValue } from "@mantine/hooks";

import {
  Grid,
  Typography,
  Paper,
  MenuItem,
  SelectChangeEvent,
  Box,
  CircularProgress,
  IconButton,
} from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import MonitorIcon from "@mui/icons-material/Monitor";
import { colors } from "@zapalacorp/tokens";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "Components/Form";
import { Color } from "Components/Form/Button";
import Checkbox from "Components/Form/Checkbox";
import Input from "Components/Form/Input";
import Select from "Components/Form/Select";
import { Breadcrumb } from "Components/Generic/Breadcrumb";
import InfoIcon from "Components/Generic/Icons/Info";
import DocumentUpload from "Components/Generic/Icons/DocumentUpload";
import { AppSwitch } from "Components/Generic/AppSwitch";
import BadgeStatus from "Components/Generic/BadgeStatus";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { templateTags, PRIVATE_ROUTES } from "Constants";
import JoditEditor, { IJoditEditorProps } from "jodit-react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { handleInsertTemplateTag, removeHTML } from "Util/joditEditor";
import {
  replaceTemplateTagsForPreview,
  convertHtmlToPlainText,
} from "Util/notificationTemplatePreview";
import { useAppState } from "Context/StateContext";
import { NotificationTrigger } from "Models/notification.trigger";
import NotificationTriggerService from "Services/notification.trigger.service";
import { PushNotificationTarget } from "Util/enums";

import CommunicationCentreService from "Services/communication.centre.service";
import UploadService from "Services/upload.service";
import { NotificationTemplate } from "Models/notification.template";
import { NotificationAttachment } from "Models/notification.attachment";
import AttachmentUpload, { UploadedAttachment } from "./components/AttachmentUpload";
import SystemReports from "./components/SystemReports";
import FullScreenEmailPortalPreview, {
  InboxMockItem,
} from "./components/FullScreenEmailPortalPreview";
import SMSPreview from "Components/Generic/SMSPreview";
import PushNotificationPreview from "Components/Generic/PushNotificationPreview";
import useStyles from "./styles";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";
import {
  getAllowedReportNamesForAvailableIn,
  getSystemReportsForAvailableIn,
  isReportAllowedForAvailableIn,
} from "@/constants/communicationCentreSystemReports";

interface TemplateFormData {
  templateName: string;
  subject: string;
  fromName: string;
  email: string;
  status: string;
  availableIn: string[];
  isDefault: boolean;
  emailEnabled: boolean;
  emailContent: string;
  smsEnabled: boolean;
  smsContent: string;
  attachmentsEnabled: boolean;
  pushWebEnabled: boolean;
  pushSalesAppEnabled: boolean;
  pushOperationsAppEnabled: boolean;
  // Push notification specific fields
  pushTitle: string;
  pushContent: string;
  plainTextMode: boolean;
  showHeader: boolean;
  showCopyrightText: boolean;
  customerLogo: boolean;
  shipeeziBranding: boolean;
}

type FormErrors = Partial<Record<keyof TemplateFormData, string>> & {
  systemReports?: string;
};

const parseTemplateName = (name: string): { baseName: string; number: number } => {
  if (!name || !name.trim()) {
    return { baseName: "", number: 1 };
  }

  const match = name.match(/^(.*?)\s*\((\d+)\)\s*$/);

  if (match) {
    const baseName = match[1].trim();
    const number = parseInt(match[2], 10);
    return { baseName, number };
  }

  return { baseName: name.trim(), number: 1 };
};

const generateNextTemplateName = (
  baseName: string,
  existingTemplates: NotificationTemplate[]
): string => {
  const highestVersion = existingTemplates.reduce((acc, template) => {
    const parsed = parseTemplateName(template.name);
    if (parsed.baseName === baseName) {
      acc = Math.max(acc, parsed.number);
    }
    return acc;
  }, 0);

  if (highestVersion === 0) {
    return `${baseName} (2)`;
  }
  return `${baseName} (${highestVersion + 1})`;
};

const NewTemplatePage = () => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { currentUser } = useAppState();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<TemplateFormData>({
    templateName: "",
    subject: "",
    fromName: "",
    email: "",
    status: "Active",
    availableIn: ["Estimate"],
    isDefault: false,
    emailEnabled: false,
    emailContent: "",
    smsEnabled: false,
    smsContent: "",
    attachmentsEnabled: false,
    pushWebEnabled: false,
    pushSalesAppEnabled: false,
    pushOperationsAppEnabled: false,
    pushTitle: "",
    pushContent: "",
    plainTextMode: false,
    showHeader: true,
    showCopyrightText: true,
    customerLogo: false,
    shipeeziBranding: true,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [originalTemplate, setOriginalTemplate] = useState<NotificationTemplate | null>(null);
  const [notificationTriggers, setNotificationTriggers] = useState<NotificationTrigger[]>([]);
  const [isLoadingTriggers, setIsLoadingTriggers] = useState(false);
  const [userUploadedAttachments, setUserUploadedAttachments] = useState<UploadedAttachment[]>([]);
  const [selectedSystemReportNames, setSelectedSystemReportNames] = useState<string[]>([]);
  const [pushPreviewMode, setPushPreviewMode] = useState<"mobile" | "web">("mobile");
  const [isEmailPreviewOpen, setIsEmailPreviewOpen] = useState(false);

  const tWithFallback = useCallback(
    (key: string, fallback: string) => {
      const value = translate(key);
      return value === key ? fallback : value;
    },
    [translate]
  );

  const inboxMockItems = useMemo<InboxMockItem[]>(
    () => [
      {
        id: "email-1",
        sender: formData.fromName || "Shipeezi",
        subject: formData.subject || tWithFallback("emailTemplate", "Email template"),
        snippet: tWithFallback("preview", "Preview of the selected template"),
        time: "09:41",
      },
      {
        id: "email-2",
        sender: "Operations Team",
        subject: tWithFallback("shipmentUpdate", "Shipment update"),
        snippet: tWithFallback("preview", "Preview of the selected template"),
        time: "08:13",
      },
      {
        id: "email-3",
        sender: "Sales Team",
        subject: tWithFallback("quoteUpdate", "Quote update"),
        snippet: tWithFallback("preview", "Preview of the selected template"),
        time: "Yesterday",
      },
      {
        id: "email-4",
        sender: "Support Team",
        subject: tWithFallback("newTemplate", "New template"),
        snippet: tWithFallback("preview", "Preview of the selected template"),
        time: "Yesterday",
      },
    ],
    [formData.fromName, formData.subject, tWithFallback]
  );

  const emailLayoutOptions = useMemo(
    () =>
      [
        { field: "plainTextMode", label: tWithFallback("plainText", "Plain text") },
        { field: "showHeader", label: tWithFallback("header", "Header") },
        { field: "showCopyrightText", label: tWithFallback("copyrightText", "Copyright text") },
        { field: "customerLogo", label: tWithFallback("customerLogo", "Customer logo") },
        {
          field: "shipeeziBranding",
          label: tWithFallback("shipeeziBranding", "Shipeezi branding"),
        },
      ] as const,
    [tWithFallback]
  );

  const textEditorRef = useRef(null);
  const smsTextEditorRef = useRef(null);
  const pushTextEditorRef = useRef(null);

  useEffect(() => {
    const state = location.state as any;

    const initializeTemplate = async () => {
      if (state?.isDuplicate && state?.duplicateTemplate) {
        setIsDuplicate(true);
        adaptNotificationTriggers(state.duplicateTemplate);
        setOriginalTemplate(state.duplicateTemplate);
        await populateFormFromTemplate(state.duplicateTemplate, true);
      } else if (state?.isEdit && state?.editTemplate) {
        setIsDuplicate(false);
        adaptNotificationTriggers(state.editTemplate);
        setOriginalTemplate(state.editTemplate);
        await populateFormFromTemplate(state.editTemplate, false);
      }
    };

    initializeTemplate();
  }, [location.state]);

  useEffect(() => {
    const fetchNotificationTriggers = async () => {
      if (!currentUser?.company?.profile?.id) {
        return;
      }

      setIsLoadingTriggers(true);
      try {
        const response = await NotificationTriggerService.getAll({
          companyProfileId: currentUser.company.profile.id,
          active: 1,
          searchByOr: JSON.stringify([
            {
              company_id: currentUser.company.id,
            },
            {
              is_system_default: 1,
            },
          ]),
        });

        if (response.data) {
          console.log("📥 Fetched triggers from API:", response.data);
          console.log(
            "📋 Original template notification triggers:",
            originalTemplate?.notificationTriggers
          );

          response.data.forEach((trigger) => {
            if (originalTemplate?.notificationTriggers?.includes(trigger.id)) {
              trigger.isSelected = true;
            } else {
              trigger.isSelected = false;
            }
          });

          console.log("✅ Triggers after setting isSelected:", response.data);
          setNotificationTriggers(response.data);
        }
      } catch (error) {
        console.error("Error fetching notification triggers:", error);
        NotificationToast.error(translate("errorFetchingTriggers"));
      } finally {
        setIsLoadingTriggers(false);
      }
    };

    fetchNotificationTriggers();
  }, [currentUser?.company?.profile?.id, translate, originalTemplate]);

  useEffect(() => {
    console.log("🔄 Current notification triggers state:", notificationTriggers);
  }, [notificationTriggers]);

  const handleSelectAllTriggers = (_event: React.ChangeEvent<any>, checked: boolean) => {
    console.log(`🔘 Select All clicked: ${checked ? "CHECKED" : "UNCHECKED"}`);

    setNotificationTriggers((prev) => {
      const updated = prev.map((trigger) => ({
        ...trigger,
        isSelected: checked,
      }));
      console.log("📝 Updated triggers after Select All:", updated);
      return updated;
    });
  };

  const handleSelectTrigger = (triggerId: number) => {
    setNotificationTriggers((prev) => {
      const updated = prev.map((trigger) => {
        if (trigger.id === triggerId) {
          const newValue = !trigger.isSelected;
          console.log(`☑️ Trigger ${triggerId} toggled: ${trigger.isSelected} → ${newValue}`);
          return { ...trigger, isSelected: newValue };
        }
        return trigger;
      });
      return updated;
    });
  };

  const handleEmailOptionToggle =
    (
      field:
        | "plainTextMode"
        | "showHeader"
        | "showCopyrightText"
        | "customerLogo"
        | "shipeeziBranding"
    ) =>
    (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setFormData((prev) => {
        const next = { ...prev, [field]: checked };

        if (field === "customerLogo") {
          next.customerLogo = checked;
          next.emailContent = checked
            ? prev.emailContent.replace(/#TAG_SHIPEEZI_LOGO/g, "#TAG_CUSTOMER_LOGO")
            : prev.emailContent.replace(/#TAG_CUSTOMER_LOGO/g, "#TAG_SHIPEEZI_LOGO");
        }

        return next;
      });
    };

  const hasLogoTag = useMemo(
    () =>
      formData.emailContent.includes("#TAG_SHIPEEZI_LOGO") ||
      formData.emailContent.includes("#TAG_CUSTOMER_LOGO"),
    [formData.emailContent]
  );

  const config: IJoditEditorProps["config"] = useMemo(
    () => ({
      height: "584px",
      toolbarButtonSize: "middle",
      removeButtons: ["fullsize"],
      className: classes.joditOverrides,
      buttons: [
        "fontsize",
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "source",
        "|",
        "left",
        "center",
        "right",
        "justify",
        "ul",
        "ol",
        "|",
        "image",
        "table",
        "link",
        "|",
        "undo",
        "redo",
      ],
      controls: {
        customer: {
          name: translate("customer") as string,
          exec: handleInsertTemplateTag,
          list: templateTags.customer,
        },
        quote: {
          name: translate("quote") as string,
          exec: handleInsertTemplateTag,
          list: templateTags.quote,
        },
        order: {
          name: translate("order") as string,
          exec: handleInsertTemplateTag,
          list: templateTags.order,
        },
        shipment: {
          name: translate("shipment") as string,
          exec: handleInsertTemplateTag,
          list: templateTags.shipment,
        },
      },
      uploader: { insertImageAsBase64URI: true },
      extraButtons: ["quote", "order", "shipment", "customer"],
    }),
    [classes.joditOverrides, translate]
  );

  const smsConfig: IJoditEditorProps["config"] = useMemo(
    () =>
      ({
        height: 360,
        maxHeight: 360,
        minHeight: 360,
        className: "joditSMS",
        toolbarButtonSize: "middle",
        removeButtons: ["fullsize"],
        buttons: ["bold", "italic", "underline", "fontsize", "link", "undo", "redo"],
        controls: {
          customer: {
            name: translate("customer") as string,
            exec: handleInsertTemplateTag,
            list: templateTags.customer,
          },
          quote: {
            name: translate("quote") as string,
            exec: handleInsertTemplateTag,
            list: templateTags.quote,
          },
          order: {
            name: translate("order") as string,
            exec: handleInsertTemplateTag,
            list: templateTags.order,
          },
          shipment: {
            name: translate("shipment") as string,
            exec: handleInsertTemplateTag,
            list: templateTags.shipment,
          },
        },
        uploader: {
          insertImageAsBase64URI: true,
        },
        extraButtons: ["quote", "order", "shipment", "customer"],
        enter: "br",
        disablePlugins: "resizer,drag-and-drop-element",
      } as IJoditEditorProps["config"]),
    [translate]
  );

  // Memoized onChange handlers for editors
  const handleEmailContentChange = useCallback((newContent: string) => {
    setFormData((prev) => ({ ...prev, emailContent: newContent }));
  }, []);

  const handleSmsContentChange = useCallback((newContent: string) => {
    setFormData((prev) => ({ ...prev, smsContent: newContent }));
  }, []);

  const handlePushContentChange = useCallback((newContent: string) => {
    setFormData((prev) => ({ ...prev, pushContent: newContent }));
  }, []);

  // Debounced content for character counts (Tier 2)
  const [debouncedSmsContent] = useDebouncedValue(formData.smsContent, 300);
  const [debouncedPushContent] = useDebouncedValue(formData.pushContent, 300);

  // Memoized character counts
  const smsCharCount = useMemo(() => {
    const plainText = removeHTML(debouncedSmsContent || "");
    return plainText.length;
  }, [debouncedSmsContent]);

  const smsCharExceeded = useMemo(() => smsCharCount > 168, [smsCharCount]);

  const pushCharCount = useMemo(() => {
    const plainText = removeHTML(debouncedPushContent || "");
    return plainText.length;
  }, [debouncedPushContent]);

  const pushCharExceeded = useMemo(() => pushCharCount > 178, [pushCharCount]);

  const smsPreviewContent = useMemo(() => {
    if (!formData.smsContent) return "";
    const plainText = removeHTML(formData.smsContent);
    if (plainText.length <= 168) return formData.smsContent;

    return plainText.substring(0, 168);
  }, [formData.smsContent]);

  const pushPreviewContent = useMemo(() => {
    if (!formData.pushContent) return "";
    const plainText = removeHTML(formData.pushContent);
    if (plainText.length <= 178) return formData.pushContent;

    return plainText.substring(0, 178);
  }, [formData.pushContent]);

  const handleInputChange =
    (field: keyof TemplateFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      setFormData((prev) => ({ ...prev, [field]: value }));

      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const handleSelectChange =
    (field: keyof TemplateFormData) => (event: SelectChangeEvent<unknown>) => {
      const value = event.target.value as string;

      setFormData((prev) => ({ ...prev, [field]: value }));

      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const handleMultiSelectChange =
    (field: keyof TemplateFormData) => (event: SelectChangeEvent<unknown>) => {
      const value = event.target.value as string[];

      setFormData((prev) => {
        const next = { ...prev, [field]: value };
        if (field === "availableIn") {
          const allowed = getAllowedReportNamesForAvailableIn(value);
          setSelectedSystemReportNames((current) =>
            current.filter((name) => allowed.has(name))
          );
        }
        return next;
      });

      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
      if (field === "availableIn" && errors.systemReports) {
        setErrors((prev) => ({ ...prev, systemReports: undefined }));
      }
    };

  const visibleSystemReports = useMemo(
    () => getSystemReportsForAvailableIn(formData.availableIn),
    [formData.availableIn]
  );

  const handleSwitchChange =
    (field: keyof TemplateFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;

      setFormData((prev) => ({ ...prev, [field]: checked }));
    };

  const validateTemplateName = async (): Promise<{ isValid: boolean; errorMessage?: string }> => {
    const templateName = formData.templateName.trim();

    if (!templateName || templateName.length === 0) {
      return { isValid: false, errorMessage: translate("thisFieldIsRequired") };
    }

    if (!currentUser?.companyId) {
      return { isValid: false, errorMessage: translate("templateNameAlreadyExists") };
    }

    try {
      const response = await CommunicationCentreService.validateTemplateName(
        templateName,
        currentUser.companyId,
        originalTemplate && originalTemplate.id && !isDuplicate ? originalTemplate.id : undefined
      );

      const { valid } = response.data;
      if (valid) {
        return { isValid: true };
      }

      return { isValid: false, errorMessage: translate("templateNameAlreadyExists") };
    } catch (error) {
      console.error("Error validating template name:", error);
      return { isValid: false, errorMessage: translate("errorValidatingTemplateName") };
    }
  };

  const validateForm = async (): Promise<boolean> => {
    const newErrors: FormErrors = {};

    if (!formData.subject.trim()) {
      newErrors.subject = translate("thisFieldIsRequired");
    }
    if (formData.emailEnabled) {
      if (!formData.fromName.trim()) {
        newErrors.fromName = translate("thisFieldIsRequired");
      }
      if (!formData.email.trim()) {
        newErrors.email = translate("thisFieldIsRequired");
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = translate("invalidEmailFormat");
      }
    }
    if (formData.smsEnabled) {
      const plainText = removeHTML(formData.smsContent || "");
      const charCount = plainText.length;
      if (charCount > 168) {
        newErrors.smsContent =
          translate("smsCharacterLimitExceeded") || "SMS content exceeds 168 character limit";
      }
    }
    if (
      formData.pushWebEnabled ||
      formData.pushSalesAppEnabled ||
      formData.pushOperationsAppEnabled
    ) {
      const plainText = removeHTML(formData.pushContent || "");
      const charCount = plainText.length;
      if (charCount > 178) {
        newErrors.pushContent =
          translate("pushCharacterLimitExceeded") ||
          "Push notification content exceeds 178 character limit";
      }
    }
    if (!formData.status.trim()) {
      newErrors.status = translate("thisFieldIsRequired");
    }
    if (!formData.availableIn.length) {
      newErrors.availableIn = translate("thisFieldIsRequired");
    }

    if (formData.attachmentsEnabled && selectedSystemReportNames.length > 0) {
      const hasInvalidReport = selectedSystemReportNames.some(
        (name) => !isReportAllowedForAvailableIn(name, formData.availableIn)
      );
      if (hasInvalidReport) {
        newErrors.systemReports =
          translate("invalidSystemReportForAvailableIn") ||
          "One or more system reports are not valid for the selected Available in options.";
      }
    }

    const nameValidation = await validateTemplateName();
    if (!nameValidation.isValid) {
      newErrors.templateName = nameValidation.errorMessage;
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const adaptNotificationTriggers = (template?: any) => {
    if (!template) return;
    if (Array.isArray(template.notificationTriggers) && template.notificationTriggers.length > 0) {
      template.notificationTriggers = template.notificationTriggers.map(
        (t: NotificationTrigger) => t.id
      );
    } else {
      template.notificationTriggers = [];
    }
  };

  const adaptEmailLogo = (html: string): string => {
    const SHIPEEZI_LOGO_IMG = `<img style="margin:0;border:0;padding:0;display:block;width:121px;height:29px;" src="https://shipeezi-images.s3.ap-southeast-2.amazonaws.com/shipeezi-logo.png" alt="Shipeezi logo" width="121" height="29" />`;
    const customerLogoSrc = currentUser?.company?.logo?.mediaLink;
    const CUSTOMER_LOGO_IMG = customerLogoSrc
      ? `<img style="margin:0;border:0;padding:0;display:block;width:121px;height:29px;" src="${customerLogoSrc}" alt="Customer logo" width="121" height="29" />`
      : SHIPEEZI_LOGO_IMG;

    return html
      .replace(/#TAG_SHIPEEZI_LOGO/g, SHIPEEZI_LOGO_IMG)
      .replace(/#TAG_CUSTOMER_LOGO/g, CUSTOMER_LOGO_IMG);
  };

  const extractBodyFromHtml = (html: string): string => {
    if (!html) return "";

    const mainMatch = html.match(/<main[^>]*>([\s\S]*?)<\/main>/i);
    if (mainMatch && mainMatch[1]) {
      return mainMatch[1].trim();
    }

    const hasHeader = html.includes("<header");
    const hasFooter = html.includes("<footer");
    const hasTableWrapper = html.includes("<table") && html.includes("cellpadding");

    if (hasHeader || hasFooter || hasTableWrapper) {
      let bodyHtml = html.replace(/<header[^>]*>[\s\S]*?<\/header>/gi, "");
      bodyHtml = bodyHtml.replace(/<footer[^>]*>[\s\S]*?<\/footer>/gi, "");
      bodyHtml = bodyHtml.replace(/<table[^>]*>[\s\S]*?<td[^>]*>/gi, "");
      bodyHtml = bodyHtml.replace(/<\/td>[\s\S]*?<\/tr>[\s\S]*?<\/table>/gi, "");
      return bodyHtml.trim();
    }

    return html.trim();
  };

  const detectEmailOptionsFromHtml = (html: string) => {
    if (!html) {
      return {
        showHeader: true,
        showCopyrightText: true,
        shipeeziBranding: true,
      };
    }

    const hasHeader = /<header[^>]*>/i.test(html);
    const hasFooter = /<footer[^>]*>/i.test(html);

    let showCopyrightText = false;

    if (hasFooter) {
      const footerMatch = html.match(/<footer[^>]*>([\s\S]*?)<\/footer>/i);
      if (footerMatch && footerMatch[1]) {
        const footerContent = footerMatch[1];
        showCopyrightText = /©|copyright/i.test(footerContent);
      }
    }

    return {
      showHeader: hasHeader,
      showCopyrightText,
      shipeeziBranding: true,
    };
  };

  const extractFilenameFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.split("/").pop() || "file";
      return decodeURIComponent(filename);
    } catch {
      const pathname = url.split("?")[0].split("#")[0];
      const filename = pathname.split("/").pop() || "file";
      return decodeURIComponent(filename);
    }
  };

  const populateFormFromTemplate = async (
    template?: NotificationTemplate,
    isTemplateDuplicate: boolean = false
  ) => {
    if (!template) return;

    const notificationTypes = Array.isArray(template.notificationTypes)
      ? template.notificationTypes
      : [];
    const isEmailTemplate = notificationTypes.includes("email");
    const isSmsTemplate = notificationTypes.includes("sms");
    const isPushTemplate = notificationTypes.includes("push");

    const pushTargets = Array.isArray(template.pushNotificationTarget)
      ? template.pushNotificationTarget
      : [];

    const savedHtml = (template as any).html || "";
    const bodyContent = extractBodyFromHtml(savedHtml);
    const emailOptions = detectEmailOptionsFromHtml(savedHtml);

    const attachments = template.notificationAttachments || [];
    const userAttachments: UploadedAttachment[] = attachments
      .filter((att: NotificationAttachment) => att.fileUrl)
      .map((att: NotificationAttachment) => {
        const filename = att.fileUrl ? extractFilenameFromUrl(att.fileUrl) : "file";
        return {
          id: att.id?.toString(),
          name: filename,
          size: 0,
          fileUrl: att.fileUrl,
          uri: att.fileUrl,
          file: new File([], filename), // Placeholder file
        };
      });

    const loadedAvailableIn = (Array.isArray(template.availableIn) ? template.availableIn : [])
      .map((v) => (v === "Quote" ? "Estimate" : v))
      .filter((v) => v !== "Invoice");
    const systemReportNames = attachments
      .filter((att: NotificationAttachment) => att.reportId || att.report?.name)
      .map(
        (att: NotificationAttachment) =>
          att.report?.name || att.reportName || ""
      )
      .filter((name): name is string => Boolean(name))
      .filter((name) => isReportAllowedForAvailableIn(name, loadedAvailableIn));

    setUserUploadedAttachments(userAttachments);
    setSelectedSystemReportNames(systemReportNames);

    let newTemplateName = template.name;
    if (isTemplateDuplicate) {
      try {
        const response = await CommunicationCentreService.getAll({
          companyId: currentUser?.companyId,
          attributes: ["name"],
        });

        const existingTemplates = response?.data || [];

        const { baseName, number } = parseTemplateName(template.name);

        if (number > 1) {
          newTemplateName = generateNextTemplateName(baseName, existingTemplates);
        } else {
          newTemplateName = `${baseName} (2)`;
        }

        console.log(`🔢 Template name generation: "${template.name}" → "${newTemplateName}"`);
      } catch (error) {
        console.error("Error fetching templates for name generation:", error);
        // Fallback to simple copy suffix if API fails
        newTemplateName = `${template.name} (2)`;
      }
    }

    setFormData((prev) => ({
      ...prev,
      templateName: newTemplateName,
      subject: template.subject,
      fromName: template.fromName || "",
      email: template.fromEmail || "",
      status: template.status.charAt(0).toUpperCase() + template.status.slice(1),
      availableIn: (Array.isArray(template.availableIn) ? template.availableIn : [])
        .map((v) => (v === "Quote" ? "Estimate" : v))
        .filter((v) => v !== "Invoice"),
      isDefault: template.default || false,
      emailEnabled: Boolean(isEmailTemplate),
      emailContent: bodyContent,
      smsEnabled: Boolean(isSmsTemplate),
      smsContent: template.content || "",
      attachmentsEnabled: attachments.length > 0 || systemReportNames.length > 0,
      pushWebEnabled: pushTargets.includes(PushNotificationTarget.WEB),
      pushSalesAppEnabled: pushTargets.includes(PushNotificationTarget.SALES_APP),
      pushOperationsAppEnabled: pushTargets.includes(PushNotificationTarget.OPERATIONS_APP),
      pushTitle: template.pushTitle || template.subject || "",
      pushContent: template.pushContent || "",
      showHeader: emailOptions.showHeader,
      showCopyrightText: emailOptions.showCopyrightText,
      customerLogo: savedHtml.includes("#TAG_CUSTOMER_LOGO"),
      shipeeziBranding: true,
    }));
  };

  const composeEmailHtml = (showRawTags: boolean = true, preview: boolean = false) => {
    const bodyContent = replaceTemplateTagsForPreview(
      formData.emailContent || "",
      showRawTags,
      true
    );

    const headerLogo = formData.customerLogo ? "#TAG_CUSTOMER_LOGO" : "#TAG_SHIPEEZI_LOGO";
    const header = formData.showHeader
      ? `<header style="padding:24px 32px;background:#F5F7FA;border-bottom:1px solid #E4E7EC;">
            <table width="100%" cellpadding="0" cellspacing="0">
              <tr>
                <td>
                  ${preview ? adaptEmailLogo(headerLogo) : headerLogo}
                </td>
                <td style="font-family:Manrope,Arial,sans-serif;font-size:14px;color:#475467;text-align:right;">
                  ${formData.fromName || formData.email || "notifications@shipeezi.com"}
                </td>
              </tr>
            </table>
          </header>`
      : "";

    const footerParts: string[] = [];

    if (formData.showCopyrightText) {
      footerParts.push(`© ${new Date().getFullYear()} ${formData.fromName || "Shipeezi"}`);
    }

    if (formData.shipeeziBranding) {
      footerParts.push("Powered by Shipeezi");
    }

    const footer =
      footerParts.length > 0
        ? `<footer style="padding:24px 32px;background:#F5F7FA;border-top:1px solid #E4E7EC;text-align:center;font-family:Manrope,Arial,sans-serif;font-size:12px;color:#475467;">
            ${footerParts.join("<br/>")}
          </footer>`
        : "";

    return `
      <table width="100%" cellpadding="0" cellspacing="0" style="font-family:Manrope,Arial,sans-serif;background:#FFFFFF;">
        <tr>
          <td>
            ${header}
            <main style="padding:32px;">
              ${preview ? adaptEmailLogo(bodyContent) : bodyContent}
            </main>
            ${footer}
          </td>
        </tr>
      </table>
    `;
  };

  const emailPreviewHtml = useMemo(() => {
    return composeEmailHtml(true, true);
  }, [
    formData.emailContent,
    formData.customerLogo,
    formData.showHeader,
    formData.showCopyrightText,
    formData.shipeeziBranding,
    formData.fromName,
    formData.email,
    currentUser?.company?.logo?.mediaLink,
  ]);

  const transformFormDataToTemplate = (
    attachments: UploadedAttachment[] = userUploadedAttachments
  ): NotificationTemplate & { attachments?: NotificationAttachment[] } => {
    const notificationTypes: string[] = [];

    if (formData.emailEnabled) {
      notificationTypes.push("email");
    }
    if (formData.smsEnabled) {
      notificationTypes.push("sms");
    }
    if (
      formData.pushWebEnabled ||
      formData.pushSalesAppEnabled ||
      formData.pushOperationsAppEnabled
    ) {
      notificationTypes.push("push");
    }

    const pushNotificationTargets: string[] = [];
    if (formData.pushWebEnabled) {
      pushNotificationTargets.push(PushNotificationTarget.WEB);
    }
    if (formData.pushOperationsAppEnabled) {
      pushNotificationTargets.push(PushNotificationTarget.OPERATIONS_APP);
    }
    if (formData.pushSalesAppEnabled) {
      pushNotificationTargets.push(PushNotificationTarget.SALES_APP);
    }

    const notificationAttachments: NotificationAttachment[] = [];

    attachments.forEach((att) => {
      if (att.fileUrl) {
        notificationAttachments.push({
          fileUrl: att.fileUrl,
        });
      }
    });

    selectedSystemReportNames.forEach((reportName) => {
      notificationAttachments.push({
        reportName,
      });
    });

    return {
      id: 0,
      name: formData.templateName,
      subject: formData.subject,
      fromName: formData.fromName,
      fromEmail: formData.email,
      html: formData.emailEnabled ? composeEmailHtml() : "",
      content: formData.smsEnabled ? convertHtmlToPlainText(formData.smsContent) : "",
      availableIn: formData.availableIn,
      updatedAt: new Date(),
      status: formData.status.toLowerCase(),
      default: formData.isDefault,
      notificationTypes: notificationTypes.length > 0 ? notificationTypes : [],
      valid: true,
      templateAvailableOptions: [],
      companyToEmailTemplate: [],
      createdAt: new Date(),
      parentId: isDuplicate && originalTemplate ? originalTemplate.id : undefined,
      notificationTriggers: notificationTriggers.reduce((acc, trigger) => {
        if (trigger.isSelected && trigger.id) {
          acc.push(trigger.id);
        }
        return acc;
      }, [] as number[]),
      pushNotificationTarget: pushNotificationTargets,
      notificationAttachments,
      pushTitle: formData.pushTitle,
      pushContent: convertHtmlToPlainText(formData.pushContent),
    };
  };

  const isAnyPushEnabled =
    formData.pushWebEnabled || formData.pushSalesAppEnabled || formData.pushOperationsAppEnabled;

  const navigateToCommunicationCentre = () => {
    history.push(PRIVATE_ROUTES.SETTINGS, {
      tabKey: "communicationcentre",
    });
  };

  const handleOpenEmailPreview = () => {
    setIsEmailPreviewOpen(true);
  };

  const handleCloseEmailPreview = () => {
    setIsEmailPreviewOpen(false);
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      if (!(await validateForm())) {
        setIsSaving(false);
        return;
      }
      const attachmentsToUpload = userUploadedAttachments.filter(
        (att) => att.uri && !att.fileUrl && att.file
      );

      let finalAttachments = [...userUploadedAttachments];

      if (attachmentsToUpload.length > 0) {
        for (const attachment of attachmentsToUpload) {
          try {
            if (!attachment.uri || !attachment.file) {
              continue;
            }

            const splittedFileName = attachment.file.name.split(".");
            const extension = splittedFileName[splittedFileName.length - 1];
            const nameWithoutExtension = attachment.file.name.slice(
              0,
              attachment.file.name.lastIndexOf(".")
            );
            const filename = `${nameWithoutExtension}_${Date.now()}.${extension}`;
            console.log("Saving file ", filename, attachment.uri);

            const response = await UploadService.uploadFile({
              filename,
              uri: attachment.uri,
            });
            console.log("Response: ", response.data);

            console.log("Final attachments: ", finalAttachments);
            if (response.data?.url) {
              console.log("Updating attachment with fileUrl: ", response.data.url);
              finalAttachments = finalAttachments.map((att) => {
                if (att.id === attachment.id) {
                  return { ...att, fileUrl: response.data.url };
                }
                return att;
              });
            }
            console.log("Final attachments after update: ", finalAttachments);
          } catch (error) {
            console.error("Error uploading file:", error);
            NotificationToast.error(translate("errorUploadingFile") || "Error uploading file");
            setIsSaving(false);
            return;
          }
        }

        setUserUploadedAttachments(finalAttachments);
      }

      const templateData = transformFormDataToTemplate(finalAttachments);
      let response;

      if (originalTemplate && !isDuplicate) {
        response = await CommunicationCentreService.update(originalTemplate.id!, templateData);
        if (response.data) {
          NotificationToast.success(translate("templateUpdatedSuccessfully"));
        }
      } else {
        response = await CommunicationCentreService.create(templateData);
        if (response.data) {
          const successMessage = isDuplicate
            ? translate("templateDuplicatedSuccessfully")
            : translate("templateCreatedSuccessfully");
          NotificationToast.success(successMessage);
        }
      }

      if (response.data) {
        await queryClient.invalidateQueries(["communication-centre.list"]);
        await queryClient.invalidateQueries(["communication-centre.count"]);

        navigateToCommunicationCentre();
      }
    } catch (error: any) {
      console.error("Error saving template:", error);
      const errorMessage =
        originalTemplate && !isDuplicate
          ? translate("errorUpdatingTemplate")
          : translate("errorCreatingTemplate");

      NotificationToast.error(error?.response?.data?.message || errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigateToCommunicationCentre();
  };

  const breadcrumbItems = [
    {
      key: "settings",
      label: translate("settings"),
      onClick: () => history.push("/app/settings"),
    },
    {
      key: "communicationCentre",
      label: translate("communicationCentre"),
      onClick: navigateToCommunicationCentre,
    },
    {
      key: "template",
      label: isDuplicate
        ? `${translate("duplicateTemplate")} (${originalTemplate?.name})`
        : originalTemplate && !isDuplicate
        ? `${translate("editTemplate")} (${originalTemplate?.name})`
        : translate("newTemplate"),
    },
  ];

  return (
    <div className={classes.pageWrapper}>
      <div className={classes.breadcrumbContainer}>
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className={classes.contentContainer}>
        <Paper className={classes.paper}>
          <Box
            style={{
              display: "flex",
              padding: "24px",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "20px",
            }}
          >
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                gap: "16px",
              }}
            >
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Typography variant="h6" className={classes.sectionTitle}>
                  {translate("general")}
                </Typography>
                {isDuplicate && originalTemplate?.name && (
                  <>
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 32,
                        height: 32,
                        borderRadius: "100px",
                        backgroundColor: "#F9FAFB",
                      }}
                    >
                      <DocumentUpload width={24} height={24} color="#667085" />
                    </Box>
                    <Typography
                      style={{
                        fontFamily: "Manrope",
                        fontSize: "12px",
                        fontWeight: 600,
                        lineHeight: "16px",
                        letterSpacing: "0.5px",
                        color: "#475467",
                      }}
                    >
                      {`${tWithFallback(
                        "copyOfOriginFile",
                        "This is a copy of the original file"
                      )} "${originalTemplate.name}"`}
                    </Typography>
                  </>
                )}
              </Box>

              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    borderRadius: "100px",
                    backgroundColor: "#EFF8FF",
                  }}
                >
                  <InfoIcon width={16} height={16} color="#308CDB" />
                </Box>
                <Typography
                  style={{
                    fontFamily: "Manrope",
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: "20px",
                    letterSpacing: "0.5px",
                    color: "#475467",
                    margin: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  {translate("markAsDefault")}
                </Typography>
                <AppSwitch
                  checked={formData.isDefault}
                  onChange={handleSwitchChange("isDefault")}
                />
              </Box>
            </Box>

            <Grid container direction="row" alignItems="flex-start" spacing={2}>
              <Grid item xs={12} md={4}>
                <Input
                  label={translate("templateName")}
                  value={formData.templateName}
                  onChange={handleInputChange("templateName")}
                  placeholder={translate("enterTemplateName")}
                  fullWidth
                  required
                  error={!!errors.templateName}
                  errorMessage={errors.templateName}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Input
                  label={translate("subject")}
                  value={formData.subject}
                  onChange={handleInputChange("subject")}
                  placeholder={translate("enterSubject")}
                  fullWidth
                  required
                  error={!!errors.subject}
                  errorMessage={errors.subject}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Select
                  name="status"
                  label={translate("status")}
                  value={formData.status}
                  onChange={handleSelectChange("status")}
                  required
                  error={!!errors.status}
                  errorMessage={errors.status}
                  placeholder={translate("selectAnOption")}
                >
                  <MenuItem value="Active">{translate("active")}</MenuItem>
                  <MenuItem value="Inactive">{translate("inactive")}</MenuItem>
                </Select>
              </Grid>

              <Grid item xs={12} md={4}>
                <Input
                  label={translate("fromName")}
                  value={formData.fromName}
                  onChange={handleInputChange("fromName")}
                  placeholder={translate("enterFromName")}
                  fullWidth
                  required={formData.emailEnabled}
                  error={!!errors.fromName}
                  errorMessage={errors.fromName}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Input
                  label={translate("email")}
                  value={formData.email}
                  onChange={handleInputChange("email")}
                  placeholder={translate("enterEmail")}
                  fullWidth
                  required={formData.emailEnabled}
                  error={!!errors.email}
                  errorMessage={errors.email}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Select
                  name="availableIn"
                  label={translate("availableIn")}
                  value={formData.availableIn}
                  onChange={(handleMultiSelectChange as any)("availableIn")}
                  placeholder={translate("selectOptions")}
                  multiple
                  required
                  error={!!errors.availableIn}
                  errorMessage={errors.availableIn}
                >
                  <MenuItem value="Estimate">{translate("estimate")}</MenuItem>
                  <MenuItem value="Order">{translate("order")}</MenuItem>
                  <MenuItem value="Shipment">{translate("shipment")}</MenuItem>
                </Select>
              </Grid>
            </Grid>
          </Box>
        </Paper>
        {/* Email Template */}
        <Paper className={classes.paper}>
          <Box className={classes.sectionBody}>
            <Box className={classes.sectionHeaderRow}>
              <Typography variant="h6" className={classes.sectionTitle}>
                {translate("emailTemplate")}
              </Typography>
              <AppSwitch
                checked={formData.emailEnabled}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, emailEnabled: e.target.checked }))
                }
              />
            </Box>

            {formData.emailEnabled && (
              <Box className={classes.emailEditorLayout}>
                <Box className={classes.richTextPanel}>
                  <Box className={classes.editorWorkspace}>
                    {JoditEditor ? (
                      <JoditEditor
                        ref={textEditorRef}
                        value={formData.emailContent}
                        config={config}
                        onChange={handleEmailContentChange}
                      />
                    ) : (
                      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                        <CircularProgress />
                      </Box>
                    )}
                  </Box>
                  <Box className={classes.editorOptionRow}>
                    {emailLayoutOptions.map((option) => (
                      <Box key={option.field} className={classes.editorOption}>
                        <Checkbox
                          checked={formData[option.field]}
                          onChange={handleEmailOptionToggle(option.field)}
                          disabled={
                            option.field === "shipeeziBranding" ||
                            (option.field === "customerLogo" && !hasLogoTag && !formData.showHeader)
                          }
                        />
                        <Typography className={classes.editorOptionLabel}>
                          {option.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box className={classes.previewPanel}>
                  <Box className={classes.previewBrowserChrome}>
                    <Box className={classes.previewTrafficLights}>
                      <Box
                        className={classes.previewTrafficDot}
                        sx={{ backgroundColor: "#f97066" }}
                      />
                      <Box
                        className={classes.previewTrafficDot}
                        sx={{ backgroundColor: "#fdb022" }}
                      />
                      <Box
                        className={classes.previewTrafficDot}
                        sx={{ backgroundColor: "#32d583" }}
                      />
                    </Box>
                    <Box className={classes.previewUrl}>
                      {formData.email || "https://yourmail.com/"}
                    </Box>
                    <IconButton
                      size="small"
                      className={classes.previewChromeIcon}
                      aria-label={tWithFallback("openPreview", "Open preview")}
                      onClick={handleOpenEmailPreview}
                    >
                      <OpenInFullIcon fontSize="inherit" />
                    </IconButton>
                  </Box>

                  <Box className={classes.previewBodyShell}>
                    <div
                      className={classes.previewHtml}
                      dangerouslySetInnerHTML={{
                        __html: emailPreviewHtml,
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
        <FullScreenEmailPortalPreview
          open={isEmailPreviewOpen}
          onClose={handleCloseEmailPreview}
          email={formData.email}
          emailPreviewHtml={emailPreviewHtml}
          inboxMockItems={inboxMockItems}
          tWithFallback={tWithFallback}
        />
        {/* Attachments */}
        <Paper className={classes.paper}>
          <Box
            style={{
              display: "flex",
              padding: "24px",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "20px",
            }}
          >
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography variant="h6" className={classes.sectionTitle}>
                {translate("attachments")}
              </Typography>
              <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <AppSwitch
                  checked={formData.attachmentsEnabled}
                  onChange={handleSwitchChange("attachmentsEnabled")}
                />
              </Box>
            </Box>

            {formData.attachmentsEnabled && (
              <Box className={classes.attachmentsContainer}>
                <AttachmentUpload
                  attachments={userUploadedAttachments}
                  onAttachmentsChange={setUserUploadedAttachments}
                  disabled={isSaving}
                />
                {visibleSystemReports.length > 0 && (
                  <SystemReports
                    availableIn={formData.availableIn}
                    selectedReportNames={selectedSystemReportNames}
                    onSelectedReportNamesChange={setSelectedSystemReportNames}
                    disabled={isSaving}
                  />
                )}
                {errors.systemReports && (
                  <Typography color="error" variant="body2">
                    {errors.systemReports}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Paper>
        {/* SMS */}
        <Paper className={classes.paper}>
          <Box
            style={{
              display: "flex",
              padding: "24px",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "20px",
            }}
          >
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  {translate("smsTemplate")}
                </Typography>
                {isDuplicate && originalTemplate?.notificationTypes?.includes("sms") && (
                  <Box
                    style={{
                      backgroundColor: "#E3F2FD",
                      color: "#1976D2",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: 500,
                    }}
                  >
                    {translate("preFilled")}
                  </Box>
                )}
              </Box>
              <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <AppSwitch
                  checked={formData.smsEnabled}
                  onChange={handleSwitchChange("smsEnabled")}
                />
              </Box>
            </Box>

            {/* Collapsible content - only visible when switch is on */}
            {formData.smsEnabled && (
              <Box
                style={{
                  display: "flex",
                  gap: "24px",
                  width: "100%",
                  minHeight: "250px",
                }}
              >
                <Box style={{ flex: 1, height: "100%", display: "flex", flexDirection: "column" }}>
                  <Box
                    style={{
                      backgroundColor: colors.gray50,
                      borderRadius: "4px",
                      border: `1px solid ${colors.gray200}`,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      style={{
                        backgroundColor: colors.white,
                        padding: "16px",
                        height: "400px",
                        overflow: "hidden",
                      }}
                    >
                      <JoditEditor
                        ref={smsTextEditorRef}
                        value={formData.smsContent}
                        config={smsConfig}
                        onChange={handleSmsContentChange}
                      />
                    </Box>
                    <Box
                      style={{
                        backgroundColor: colors.gray50,
                        borderTop: `1px solid ${colors.gray200}`,
                        padding: "4px 11px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                      }}
                    >
                      <Typography
                        style={{
                          fontFamily: "Manrope",
                          fontSize: "14px",
                          lineHeight: "20px",
                          color: smsCharExceeded ? "#DC2626" : "#475467",
                          fontWeight: smsCharExceeded ? 600 : 400,
                          letterSpacing: "0.25px",
                        }}
                      >
                        {`${smsCharCount} / 168 characters`}
                      </Typography>
                      <Typography
                        style={{
                          fontFamily: "Manrope",
                          fontSize: "12px",
                          lineHeight: "16px",
                          color: smsCharExceeded ? "#DC2626" : "#667085",
                          letterSpacing: "0.4px",
                        }}
                      >
                        {smsCharExceeded
                          ? "Character limit exceeded. Please reduce content to save."
                          : ""}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box
                  style={{
                    flex: 1,
                    minHeight: "300px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#F9FAFB",
                    borderRadius: "8px",
                    border: "1px solid #E4E7EC",
                    padding: "24px",
                    overflow: "hidden",
                  }}
                >
                  <SMSPreview
                    content={smsPreviewContent}
                    serviceName={formData.fromName || "TM-SHIPEEZI"}
                    showRawTags={true}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
        {/* Push Notification */}
        <Paper className={classes.paper}>
          <Box
            style={{
              display: "flex",
              padding: "24px",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "20px",
            }}
          >
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography variant="h6" className={classes.sectionTitle}>
                {translate("pushNotification")}
              </Typography>
              <Box style={{ display: "flex", alignItems: "center", gap: "24px" }}>
                <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Typography
                    style={{
                      fontFamily: "Manrope",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#475467",
                      margin: 0,
                    }}
                  >
                    {translate("web")}
                  </Typography>
                  <AppSwitch
                    checked={formData.pushWebEnabled}
                    onChange={handleSwitchChange("pushWebEnabled")}
                  />
                </Box>

                <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Typography
                    style={{
                      fontFamily: "Manrope",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#475467",
                      margin: 0,
                    }}
                  >
                    {translate("salesApp")}
                  </Typography>
                  <AppSwitch
                    checked={formData.pushSalesAppEnabled}
                    onChange={handleSwitchChange("pushSalesAppEnabled")}
                  />
                </Box>

                <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Typography
                    style={{
                      fontFamily: "Manrope",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#475467",
                      margin: 0,
                    }}
                  >
                    {translate("operationsApp")}
                  </Typography>
                  <AppSwitch
                    checked={formData.pushOperationsAppEnabled}
                    onChange={handleSwitchChange("pushOperationsAppEnabled")}
                  />
                </Box>
              </Box>
            </Box>
            {isAnyPushEnabled && (
              <Box
                style={{
                  display: "flex",
                  gap: "24px",
                  width: "100%",
                  minHeight: "400px",
                  marginTop: "16px",
                }}
              >
                <Box style={{ flex: 1, height: "100%", display: "flex", flexDirection: "column" }}>
                  <Box
                    style={{
                      backgroundColor: colors.gray50,
                      borderRadius: "4px",
                      border: `1px solid ${colors.gray200}`,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      style={{
                        padding: "12px 16px",
                        borderBottom: `1px solid ${colors.gray200}`,
                        backgroundColor: colors.white,
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <Input
                        label={tWithFallback("pushTitle", "Push title")}
                        value={formData.pushTitle}
                        onChange={handleInputChange("pushTitle")}
                        placeholder={tWithFallback("enterPushTitle", "Enter push title")}
                        fullWidth
                      />
                    </Box>
                    <Box
                      style={{
                        backgroundColor: colors.gray50,
                        borderTop: `1px solid ${colors.gray200}`,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        style={{
                          backgroundColor: colors.white,
                          padding: "16px",
                          height: "360px",
                          overflow: "hidden",
                        }}
                      >
                        <JoditEditor
                          ref={pushTextEditorRef}
                          value={formData.pushContent}
                          config={smsConfig}
                          onChange={handlePushContentChange}
                        />
                      </Box>
                      <Box
                        style={{
                          backgroundColor: colors.gray50,
                          borderTop: `1px solid ${colors.gray200}`,
                          padding: "4px 11px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                        }}
                      >
                        <Typography
                          style={{
                            fontFamily: "Manrope",
                            fontSize: "14px",
                            lineHeight: "20px",
                            color: pushCharExceeded ? "#DC2626" : "#475467",
                            fontWeight: pushCharExceeded ? 600 : 400,
                            letterSpacing: "0.25px",
                          }}
                        >
                          {`${pushCharCount} / 178 characters`}
                        </Typography>
                        <Typography
                          style={{
                            fontFamily: "Manrope",
                            fontSize: "12px",
                            lineHeight: "16px",
                            color: pushCharExceeded ? "#DC2626" : "#667085",
                            letterSpacing: "0.4px",
                          }}
                        >
                          {pushCharExceeded
                            ? "Character limit exceeded. Please reduce content to save."
                            : ""}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Box
                  style={{
                    position: "relative",
                    flex: 1,
                    minHeight: "400px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#F9FAFB",
                    borderRadius: "8px",
                    border: "1px solid #E4E7EC",
                    padding: "24px",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    style={{
                      position: "absolute",
                      bottom: "24px",
                      left: "16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      zIndex: 100,
                    }}
                  >
                    <Button
                      customColor={pushPreviewMode === "mobile" ? Color.PRIMARY : Color.WHITE}
                      onClick={() => setPushPreviewMode("mobile")}
                      style={{
                        height: "40px",
                        minWidth: "119px",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        padding: "10px 16px 10px 16px",
                        fontFamily: "Manrope",
                        fontSize: "14px",
                        fontWeight: 600,
                        letterSpacing: "0.1px",
                        lineHeight: "20px",
                        textTransform: "none",
                        ...(pushPreviewMode !== "mobile" && {
                          border: "1px solid #0055A4",
                          colocustomC: "#0055A4",
                        }),
                      }}
                    >
                      <PhoneAndroidIcon style={{ width: "20px", height: "20px" }} />
                      Mobile
                    </Button>

                    {/* Web Button */}
                    <Button
                      customColor={pushPreviewMode === "web" ? Color.PRIMARY : Color.WHITE}
                      onClick={() => setPushPreviewMode("web")}
                      style={{
                        height: "40px",
                        minWidth: "119px",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        padding: "10px 16px 10px 20px",
                        fontFamily: "Manrope",
                        fontSize: "14px",
                        fontWeight: 600,
                        letterSpacing: "0.1px",
                        lineHeight: "20px",
                        textTransform: "none",
                        ...(pushPreviewMode !== "web" && {
                          border: "1px solid #0055A4",
                          color: "#0055A4",
                        }),
                      }}
                    >
                      <MonitorIcon style={{ width: "20px", height: "20px" }} />
                      Web
                    </Button>
                  </Box>

                  <PushNotificationPreview
                    title={formData.pushTitle}
                    content={pushPreviewContent}
                    appName={formData.fromName || "Shipeezi"}
                    showRawTags={true}
                    viewMode={pushPreviewMode}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
        {/* Linked Atutomation */}
        <Paper className={classes.paper}>
          <Box
            style={{
              display: "flex",
              padding: "24px",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "20px",
            }}
          >
            <Typography variant="h6" className={classes.sectionTitle}>
              {translate("linkedAutomation")}
            </Typography>

            <Box style={{ width: "100%", overflow: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontFamily: "Manrope",
                  fontSize: "14px",
                }}
              >
                <thead>
                  <tr
                    style={{
                      backgroundColor: "#f8f9fa",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#374151",
                        borderBottom: "1px solid #e5e7eb",
                        width: "48px",
                      }}
                    >
                      <Checkbox
                        checked={
                          notificationTriggers.length > 0 &&
                          notificationTriggers.every((trigger) => trigger.isSelected)
                        }
                        control={{
                          indeterminate:
                            notificationTriggers.some((trigger) => trigger.isSelected) &&
                            !notificationTriggers.every((trigger) => trigger.isSelected),
                        }}
                        onChange={handleSelectAllTriggers}
                        disabled={notificationTriggers.length === 0}
                      />
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#374151",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      {translate("name")}
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#374151",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      {translate("automation")}
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#374151",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      {translate("lastUpdate")}
                    </th>
                    <th
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#374151",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      {translate("status")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingTriggers ? (
                    <tr>
                      <td colSpan={5} style={{ padding: "40px 16px", textAlign: "center" }}>
                        <CircularProgress size={24} />
                      </td>
                    </tr>
                  ) : notificationTriggers.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          padding: "40px 16px",
                          textAlign: "center",
                          color: "#9CA3AF",
                          fontStyle: "italic",
                        }}
                      >
                        {translate("noLinkedAutomationsFound")}
                      </td>
                    </tr>
                  ) : (
                    notificationTriggers.map((trigger) => (
                      <tr
                        key={trigger.id}
                        style={{
                          borderBottom: "1px solid #e5e7eb",
                        }}
                      >
                        <td
                          style={{
                            padding: "12px 16px",
                            textAlign: "left",
                            width: "48px",
                          }}
                        >
                          <Checkbox
                            checked={trigger.isSelected || false}
                            onChange={(_, checked) => {
                              if (checked !== trigger.isSelected) {
                                handleSelectTrigger(trigger.id!);
                              }
                            }}
                          />
                        </td>
                        <td
                          style={{
                            padding: "12px 16px",
                            color: "#374151",
                            fontFamily: "Manrope",
                            fontSize: "14px",
                          }}
                        >
                          {trigger.label || trigger.value}
                        </td>
                        <td
                          style={{
                            padding: "12px 16px",
                            color: "#374151",
                            fontFamily: "Manrope",
                            fontSize: "14px",
                          }}
                        >
                          {trigger.group ? translate(trigger.group) : "-"}
                        </td>
                        <td
                          style={{
                            padding: "12px 16px",
                            color: "#374151",
                            fontFamily: "Manrope",
                            fontSize: "14px",
                          }}
                        >
                          {trigger.updatedAt && currentUser?.preferences
                            ? formatDateWithUserPref(trigger.updatedAt, currentUser.preferences)
                            : "-"}
                        </td>
                        <td
                          style={{
                            padding: "12px 16px",
                            color: "#374151",
                            fontFamily: "Manrope",
                            fontSize: "14px",
                          }}
                        >
                          <BadgeStatus value={trigger.valid ? "Active" : "Inactive"} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </Box>
          </Box>
        </Paper>
        {/* Cancel or Save */}
        <Paper className={classes.paper}>
          <Box
            style={{
              display: "flex",
              padding: "24px",
              justifyContent: "space-between",
            }}
          >
            <Button onClick={handleCancel} customColor={Color.SEND} disabled={isSaving}>
              {translate("cancel")}
            </Button>
            <Button
              onClick={handleSave}
              customColor={Color.SECONDARY_DARK}
              disabled={isSaving}
              loading={isSaving}
            >
              {translate("save")}
            </Button>
          </Box>
        </Paper>
      </div>
    </div>
  );
};

export default NewTemplatePage;
